import {
    DELETE_CERTIFICATE_FAIL,
    DELETE_CERTIFICATE_REQUEST,
    DELETE_CERTIFICATE_SUCCESS,
    GET_CERT_CAPTURE_TOKEN_FAIL,
    GET_CERT_CAPTURE_TOKEN_REQUEST,
    GET_CERT_CAPTURE_TOKEN_SUCCESS,
    GET_CERTIFICATES_FAIL,
    GET_CERTIFICATES_REQUEST,
    GET_CERTIFICATES_SUCCESS,
    LOG_OUT_BIDDER,
} from './actions';
import { getAuthToken, getBidderId } from './user';
import { ActionWithPayload } from '@/types/redux';
import api from '@/redux/api/certCapture';
import { createSelector } from 'reselect';
import { getDeployment } from './config';
import { GlobalState } from '@/redux/rootReducer';
import { handleActions } from 'redux-actions';
import ms from 'ms';

const REDUX_STORE_TIME = ms('30m');

// reducer
const DEFAULT_STATE = {
    certCaptureToken: undefined,
    certificates: [],
    certTokenError: false,
    error: false,
    loaded: 0,
    loading: false,
};

type State = typeof DEFAULT_STATE;

export const reducer = handleActions(
    {
        [DELETE_CERTIFICATE_FAIL]: (state: State, action: ActionWithPayload<{ error: string }>) => ({
            ...state,
            error: action.error,
            loading: false,
        }),
        [DELETE_CERTIFICATE_REQUEST]: (state: State) => ({
            ...state,
            loading: true,
        }),
        [DELETE_CERTIFICATE_SUCCESS]: (state: State) => ({
            ...state,
            loading: false,
        }),
        [GET_CERT_CAPTURE_TOKEN_FAIL]: (state: State, action: ActionWithPayload<{ error: string }>) => ({
            ...state,
            certTokenError: action.error,
            loading: false,
        }),
        [GET_CERT_CAPTURE_TOKEN_REQUEST]: (state: State) => ({
            ...state,
            loading: true,
        }),
        [GET_CERT_CAPTURE_TOKEN_SUCCESS]: (state: State, action: ActionWithPayload<any>) => ({
            ...state,
            certCaptureToken: action.payload,
            certTokenError: false,
            loading: false,
        }),
        [GET_CERTIFICATES_FAIL]: (state: State, action: ActionWithPayload<{ error: string }>) => ({
            ...state,
            error: action.error,
            loading: false,
        }),
        [GET_CERTIFICATES_REQUEST]: (state: State) => ({
            ...state,
            loading: true,
        }),
        [GET_CERTIFICATES_SUCCESS]: (state: State, action: ActionWithPayload<any, any>) => ({
            ...state,
            certificates: action.payload,
            loaded: action.meta.actionTime,
            loading: false,
        }),
        [LOG_OUT_BIDDER]: () => ({
            ...DEFAULT_STATE,
        }),
    },
    DEFAULT_STATE
);

/* SELECTORS */
const stateSelector = (state) => state.certCapture;

export const getCertCaptureToken = createSelector(stateSelector, (state) => state.certCaptureToken);

export const getCertificates = createSelector(stateSelector, (state) => state.certificates || []);

export const getHasPendingOrCompleteCertificate = createSelector(stateSelector, (state) => {
    return Boolean(
        state?.certificates?.length &&
            state.certificates.find((cert) => (cert.status === 'COMPLETE' || cert.status === 'PENDING') && cert.valid)
    );
});

export const getCertificatesHaveLoaded = createSelector(stateSelector, (state) => state.loaded || state.error);

const isLoading = createSelector(stateSelector, (state) => state.loading);

export const getCertificateError = createSelector(stateSelector, (state) => state.certTokenError);

const shouldFetchCertificates = (state: GlobalState, forceUpdate) => {
    const certs = getCertificates(state);
    if (forceUpdate) {
        return true;
    }
    if (certs === null) {
        return false;
    }

    if (certs.length) {
        const loaded = getCertificatesHaveLoaded(state);
        const time = Date.now();
        const diff = time - loaded;
        if (diff < REDUX_STORE_TIME) {
            return false;
        }
    }
    const loading = isLoading(state);
    return !loading;
};

/* ACTION CREATORS */
export const fetchCertCaptureToken = () => async (dispatch: Function, getState: Function) => {
    try {
        dispatch({
            type: GET_CERT_CAPTURE_TOKEN_REQUEST,
        });
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        const bidderId = getBidderId(state);

        const response = await api.getCertCaptureToken({
            authToken,
            bidderId,
            deployment,
        });

        return dispatch({
            payload: response.payload,
            type: GET_CERT_CAPTURE_TOKEN_SUCCESS,
        });
    } catch (error) {
        return dispatch({
            error: true,
            payload: error.message || error,
            type: GET_CERT_CAPTURE_TOKEN_FAIL,
        });
    }
};

export const fetchCertificatesIfNeeded = (forceUpdate?: boolean) => async (dispatch: Function, getState: Function) => {
    if (shouldFetchCertificates(getState(), forceUpdate)) {
        return dispatch(fetchCertificates());
    }
    return Promise.resolve();
};

const fetchCertificates = () => async (dispatch: Function, getState: Function) => {
    try {
        dispatch({
            type: GET_CERTIFICATES_REQUEST,
        });
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        const bidderId = getBidderId(state);

        const response = await api.getCertificates({
            authToken,
            bidderId,
            deployment,
        });

        return dispatch({
            meta: { actionTime: Date.now() },
            payload: response.payload,
            type: GET_CERTIFICATES_SUCCESS,
        });
    } catch (error) {
        return dispatch({
            error: true,
            payload: error.message || error,
            type: GET_CERTIFICATES_FAIL,
        });
    }
};

export const fetchCertificateDownload = (certificateId: number) => async (dispatch: Function, getState: Function) => {
    try {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        const bidderId = getBidderId(state);

        const response = await api.getCertificateDownload({
            authToken,
            bidderId,
            certificateId,
            deployment,
        });

        //converts base64 response to blob
        const binaryString = window.atob(response.payload);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; ++i) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        var blob: any = new Blob([bytes], { type: 'application/pdf' });
        blob.body = bytes; //needs to be added to body for DownloadButton to work

        return blob;
    } catch (error) {
        throw error;
    }
};

export const deleteCertificate = (certificateId: number) => async (dispatch: Function, getState: Function) => {
    try {
        dispatch({
            type: DELETE_CERTIFICATE_REQUEST,
        });
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        const bidderId = getBidderId(state);

        const response = await api.deleteCertificate({
            authToken,
            bidderId,
            certificateId,
            deployment,
        });
        dispatch(fetchCertificates());

        return dispatch({
            payload: response.payload,
            type: DELETE_CERTIFICATE_SUCCESS,
        });
    } catch (error) {
        return dispatch({
            error: true,
            payload: error.message || error,
            type: DELETE_CERTIFICATE_FAIL,
        });
    }
};
